import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { BigButton } from "../design/BigButton";
import { BackupWidget } from "./BackupWidget";
import "./admin.css";

export function AdminShell() {
  const { user, logout, status } = useAuth();
  const navigate = useNavigate();

  if (status === "checking") {
    return <div className="wmleye-app" style={{ padding: 24 }}>Loading…</div>;
  }
  if (status !== "authed") {
    return <Navigate to="/login" replace />;
  }
  if (!user || !["admin", "content_editor"].includes(user.role)) {
    return (
      <div className="wmleye-app" style={{ padding: 40 }}>
        <h1>Access denied</h1>
        <p>Your account does not have admin privileges.</p>
        <BigButton onClick={() => navigate("/profiles")}>Back to parent app</BigButton>
      </div>
    );
  }

  return (
    <div className="wmleye-app admin-shell" data-testid="admin-shell">
      <aside className="admin-shell__side">
        <h2 className="admin-shell__title">Admin</h2>
        <p className="admin-shell__me">{user.email}<br /><small>{user.role}</small></p>
        <nav className="admin-shell__nav">
          <NavLink to="/admin" end data-testid="anav-overview">Overview</NavLink>
          <NavLink to="/admin/worlds" data-testid="anav-worlds">Worlds</NavLink>
          <NavLink to="/admin/levels" data-testid="anav-levels">Levels</NavLink>
          <NavLink to="/admin/debug-previews" data-testid="anav-debug-previews">Hitbox previews</NavLink>
          <NavLink to="/admin/learning-objects" data-testid="anav-los">Learning objects</NavLink>
          <NavLink to="/admin/media" data-testid="anav-media">Media library</NavLink>
          <NavLink to="/admin/generate" data-testid="anav-generate">AI generator</NavLink>
          <NavLink to="/admin/support" data-testid="anav-support">Support inbox</NavLink>
          <NavLink to="/admin/audit" data-testid="anav-audit">Audit log</NavLink>
          <NavLink to="/admin/security" data-testid="anav-security">Security &amp; sessions</NavLink>
          <NavLink to="/admin/legal-packet" data-testid="anav-legal-packet">Legal packet</NavLink>
          <NavLink to="/admin/backups" data-testid="anav-backups">Backups &amp; safety</NavLink>
          <NavLink to="/admin/settings" data-testid="anav-settings">App settings</NavLink>
        </nav>
        <div className="admin-shell__foot">
          <BigButton variant="ghost" onClick={() => navigate("/profiles")}>← Back to app</BigButton>
          <BigButton variant="coral" onClick={logout} data-testid="admin-logout">Sign out</BigButton>
        </div>
      </aside>
      <main className="admin-shell__main">
        <div className="admin-shell__main-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <IpClearanceBanner userEmail={user.email} />
          </div>
          <BackupWidget />
        </div>
        <Outlet />
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pre-launch IP-clearance banner (dismissible per-admin via localStorage).
// The dismiss state is stored per-user so a fresh admin sees it once. Clear
// localStorage to re-show; never permanently hidden globally.
// ---------------------------------------------------------------------------
function IpClearanceBanner({ userEmail }) {
  const key = `wmleye:ip-banner-dismissed:${userEmail || "anon"}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(key) === "1"; } catch { return false; }
  });
  if (dismissed) return null;
  return (
    <div
      data-testid="ip-clearance-banner"
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "0.75rem 1rem", marginBottom: "1rem",
        background: "#fff4e0", border: "2px solid #ff8a3d",
        borderRadius: 12, color: "#5a2a00",
      }}
    >
      <span aria-hidden style={{ fontSize: 22 }}>⚠️</span>
      <div style={{ flex: 1, lineHeight: 1.4 }}>
        <strong>Pre-launch requirement:</strong>{" "}
        Owner must conduct trademark and intellectual property clearance for
        the name <em>“With My Little EYE”</em> before production release.{" "}
        See <code>LAUNCH_CHECKLIST.md</code>.
      </div>
      <button
        data-testid="ip-clearance-dismiss"
        onClick={() => {
          try { localStorage.setItem(key, "1"); } catch { /* ignore */ }
          setDismissed(true);
        }}
        style={{
          background: "transparent", border: "none", cursor: "pointer",
          fontSize: 18, color: "#5a2a00", padding: "0 6px",
        }}
        aria-label="Dismiss banner"
      >×</button>
    </div>
  );
}
